import axios from 'axios';

const api = axios.create({
  baseURL: '/api',
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' }
});

// ── Response interceptor — handle auth errors globally ────────
api.interceptors.request.use((config) => {
  const schoolToken = localStorage.getItem('school_token');
  if (schoolToken && config.url && config.url.includes('/schools/')) {
    config.headers['Authorization'] = 'Bearer ' + schoolToken;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Session expired — redirect to login (but never redirect admin or school routes)
      const path = window.location.pathname;
      if (!path.startsWith('/admin') && path !== '/' && path !== '/signup') {
        const path = window.location.pathname;
        if (!path.startsWith('/admin') && !path.startsWith('/school')) {
          window.location.href = '/?session=expired';
        }
      }
    }
    return Promise.reject(error);
  }
);

// ── Auth ──────────────────────────────────────────────────────
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  logout: () => api.post('/auth/logout'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  verifyOTP: (data) => api.post('/auth/verify-otp', data),
  resetPassword: (data) => api.post('/auth/reset-password', data),
  getMe: () => api.get('/auth/me')
};

// ── Settings ──────────────────────────────────────────────────
export const settingsAPI = {
  getTheme: () => api.get('/settings/theme')
};

// ── Student ───────────────────────────────────────────────────
export const studentAPI = {
  getDashboard: () => api.get('/student/dashboard'),
  getSubjects: () => api.get('/student/subjects'),
  getChapters: (subjectId) => api.get(`/student/subjects/${subjectId}/chapters`),
  getTopicVisual: (topicId) => api.get(`/student/topics/${topicId}/visual`),
  reportError: (topicId, data) => api.post(`/student/topics/${topicId}/report`, data),
  getNotifications: () => api.get('/student/notifications'),
  uploadProfilePicture: (formData) => api.post('/student/profile/picture', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  markNotificationsRead: () => api.post('/student/notifications/read'),
  getStreakCalendar: () => api.get('/student/streak-calendar'),
  getTopic: (topicId) => api.get(`/student/topics/${topicId}`),
  getQuiz: (topicId) => api.get(`/student/topics/${topicId}/quiz`),
  submitQuiz: (topicId, data) => api.post(`/student/topics/${topicId}/quiz/submit`, data),
  getTodayRevisions: () => api.get('/student/revisions/today'),
  getAllRevisions: () => api.get('/student/revisions/all'),
  completeRevision: (revisionId) => api.post(`/student/revisions/${revisionId}/complete`),
  getProgress: () => api.get('/student/progress'),
  getWeakTopics: () => api.get('/student/weak-topics'),
  askGuruji: (data) => api.post('/student/doubt', data),
  getStreak: () => api.get('/student/streak'),
  setGoal: (count) => api.post('/student/goal', { count }),
  search: (subjectId, query) => api.get('/student/search', { params: { subjectId, q: query } }),
  updateProfile: (data) => api.patch('/student/profile', data),
  requestClassChange: (data) => api.post('/student/class-change-request', data),
  getAchievements: () => api.get('/student/achievements')
};

// ── Admin ─────────────────────────────────────────────────────
export const adminAPI = {
  login: (data) => api.post('/admin/auth/login', data),
  getDashboard: () => api.get('/admin/dashboard'),
  getStudents: (params) => api.get('/admin/students', { params }),
  getPending: (params) => api.get('/admin/students/pending', { params }),
  approveStudent: (id) => api.post(`/admin/students/${id}/approve`),
  rejectStudent: (id, reason) => api.post(`/admin/students/${id}/reject`, { reason }),
  blockStudent: (id, reason) => api.post(`/admin/students/${id}/block`, { reason }),
  unblockStudent: (id) => api.post(`/admin/students/${id}/unblock`),
  deleteStudent: (id) => api.delete(`/admin/students/${id}`),
  bulkApprove: (ids) => api.post('/admin/students/bulk-approve', { ids }),
  getSchools: () => api.get('/admin/schools'),
  getPlatformAnalytics: () => api.get('/admin/analytics/platform'),
  getSchoolAnalytics: (schoolId) => api.get(`/admin/analytics/school/${schoolId}`),
  getStudentAnalytics: (studentId) => api.get(`/admin/analytics/student/${studentId}`),
  getComparison: () => api.get('/admin/analytics/comparison'),
  getExamReadiness: (schoolId) => api.get(`/admin/analytics/exam-readiness/${schoolId}`),
  getLearningVelocity: (schoolId) => api.get(`/admin/analytics/velocity/${schoolId}`),
  getAtRiskStudents: () => api.get('/admin/analytics/at-risk'),
  sendNotification: (data) => api.post('/admin/notifications/send', data),
  getStreakCalendar: (userId) => api.get(`/admin/analytics/streak/${userId}`),
  getWeeklyReport: (schoolId) => api.get(`/admin/analytics/weekly-report/${schoolId}`),
  getNotificationSettings: () => api.get('/admin/notifications/settings'),
  updateNotificationSettings: (data) => api.put('/admin/notifications/settings', data),
  approveSchool: (id) => api.post(`/admin/schools/${id}/approve`),
  getAnalytics: () => api.get('/admin/analytics'),
  getBanners: () => api.get('/admin/banners'),
  createBanner: (data) => api.post('/admin/banners', data),
  deleteBanner: (id) => api.delete(`/admin/banners/${id}`),
  updateTheme: (data) => api.put('/admin/settings/theme', data),
  updateSettings: (data) => api.put('/admin/settings/platform', data),
  getReviewQueue: () => api.get('/admin/content/review-queue'),
  approveReviewTopic: (topicId) => api.post(`/admin/content/review-queue/${topicId}/approve`)
};

export default api;
